import { MULTIPLE_COLORS } from '@/constants/ui.constants';
import { AIFI_CHART_PRIMARY_COLOR } from '@/domains/pages/ai-proficiency/constants';
import {
  buildAnalyticsTooltipHtml,
  escapeTooltipHtml,
  PAGE_DATA_NUMBER_INLINE_CSS,
} from '@/domains/pages/shared/chart-tooltip';
import {
  PAGE_TOOLTIP_BODY_LINE_GAP,
  PAGE_TOOLTIP_BODY_TEXT_INLINE_CSS,
  PAGE_TOOLTIP_FOOTER_TEXT_INLINE_CSS,
  PAGE_TOOLTIP_SHELL_INLINE_STYLE,
  PAGE_TOOLTIP_TITLE_INLINE_CSS,
  PAGE_TOOLTIP_TOTAL_DIVIDER_INLINE_CSS,
  tooltipSwatchInlineStyle,
} from '@/domains/pages/shared/chart-tooltip.config';
import { computeDisplaySpendDelta, formatSpend, isSpendAtOrBelowBenchmark } from '@/utils/format.util';

const SPEND_POSITIVE_COLOR = MULTIPLE_COLORS[3]!;
const SPEND_NEGATIVE_COLOR = MULTIPLE_COLORS[2]!;
const SEGMENT_AVG_LINE_COLOR = 'rgba(255, 255, 255, 0.45)';

type TotalSpendOverTimeTooltipParams = {
  header: string;
  totalSpend: number | null;
};

export function buildTotalSpendOverTimeTooltipHtml({ header, totalSpend }: TotalSpendOverTimeTooltipParams): string {
  const totalSpendText = totalSpend != null ? formatSpend(totalSpend) : '-';

  return buildAnalyticsTooltipHtml({
    title: header,
    bodyLines: [{ color: AIFI_CHART_PRIMARY_COLOR, label: 'Total spend' }],
    footerLines: [{ text: `${totalSpendText} total spend` }],
  });
}

type PerTaskSpendOverTimeTooltipParams = {
  header: string;
  spendPerTask: number | null;
  seriesLabel?: string;
  avgSpendPerTask?: number | null;
  avgLineLabel?: string;
};

export function buildPerTaskSpendOverTimeTooltipHtml({
  header,
  spendPerTask,
  seriesLabel = 'AI spend per task',
  avgSpendPerTask = null,
  avgLineLabel,
}: PerTaskSpendOverTimeTooltipParams): string {
  const bodyLines = [{ color: AIFI_CHART_PRIMARY_COLOR, label: seriesLabel }];

  if (avgLineLabel) {
    bodyLines.push({ color: SEGMENT_AVG_LINE_COLOR, label: avgLineLabel });
  }

  const footerLines: Array<{ text: string; color?: string }> = [
    { text: `${spendPerTask != null ? formatSpend(spendPerTask) : '-'} ${seriesLabel}` },
  ];

  if (avgLineLabel) {
    footerLines.push({
      text: `${avgSpendPerTask != null ? formatSpend(avgSpendPerTask) : '-'} ${avgLineLabel}`,
    });

    const vsAverage =
      spendPerTask != null && avgSpendPerTask != null
        ? computeDisplaySpendDelta(spendPerTask, avgSpendPerTask)
        : null;
    const isAhead =
      spendPerTask != null && avgSpendPerTask != null && isSpendAtOrBelowBenchmark(spendPerTask, avgSpendPerTask);
    const deltaColor = vsAverage != null ? (isAhead ? SPEND_POSITIVE_COLOR : SPEND_NEGATIVE_COLOR) : undefined;
    const deltaText = vsAverage != null ? `${isAhead ? '' : '+'}${formatSpend(vsAverage)} vs. average` : '-';
    footerLines.push({ text: deltaText, color: deltaColor });
  }

  return buildAnalyticsTooltipHtml({
    title: header,
    bodyLines,
    footerLines,
  });
}

type TaskModelTooltipRow = {
  model: string;
  spend: number | null;
  spendPerTask: number | null;
  color: string;
};

type TaskModelTooltipParams = {
  taskName: string;
  rows: TaskModelTooltipRow[];
};

/** Swatch, model, spend, spend per task — model rows and the total share these columns. */
const TASK_MODEL_GRID_INLINE_CSS = [
  'display:grid',
  'grid-template-columns:8px minmax(0,1fr) auto auto',
  'align-items:center',
  'column-gap:12px',
  `row-gap:${PAGE_TOOLTIP_BODY_LINE_GAP}px`,
].join(';');

const TASK_MODEL_NUMBER_CELL_INLINE_CSS = [PAGE_DATA_NUMBER_INLINE_CSS, 'text-align:right', 'white-space:nowrap'].join(
  ';',
);

export function buildTaskModelTooltipHtml({ taskName, rows }: TaskModelTooltipParams): string {
  const visibleRows = rows.filter((row) => row.spend != null);
  const totalSpend = visibleRows.reduce((sum, row) => sum + (row.spend ?? 0), 0);
  const totalTasks = visibleRows.reduce((sum, row) => {
    if (row.spend == null || row.spendPerTask == null || row.spendPerTask <= 0) {
      return sum;
    }
    return sum + row.spend / row.spendPerTask;
  }, 0);
  const totalSpendPerTask = totalTasks > 0 ? totalSpend / totalTasks : null;

  const rowHtml = visibleRows
    .map((row) => {
      const spendText = row.spend != null ? formatSpend(row.spend) : '-';
      const spendPerTaskText = row.spendPerTask != null ? formatSpend(row.spendPerTask) : '-';
      return `
        <span style="${tooltipSwatchInlineStyle(row.color)}"></span>
        <span style="${PAGE_TOOLTIP_BODY_TEXT_INLINE_CSS}">${escapeTooltipHtml(row.model)}</span>
        <span style="${PAGE_TOOLTIP_FOOTER_TEXT_INLINE_CSS};${TASK_MODEL_NUMBER_CELL_INLINE_CSS}">${spendText}</span>
        <span style="${PAGE_TOOLTIP_BODY_TEXT_INLINE_CSS};${TASK_MODEL_NUMBER_CELL_INLINE_CSS}">${spendPerTaskText}/task</span>
      `;
    })
    .join('');

  const totalSpendText = formatSpend(totalSpend);
  const totalSpendPerTaskText = totalSpendPerTask != null ? formatSpend(totalSpendPerTask) : '-';

  const totalHtml = `
    <span style="${PAGE_TOOLTIP_TOTAL_DIVIDER_INLINE_CSS}"></span>
    <span style="${PAGE_TOOLTIP_FOOTER_TEXT_INLINE_CSS};grid-column:1 / 3;">Total</span>
    <span style="${PAGE_TOOLTIP_FOOTER_TEXT_INLINE_CSS};${TASK_MODEL_NUMBER_CELL_INLINE_CSS}">${totalSpendText}</span>
    <span style="${PAGE_TOOLTIP_BODY_TEXT_INLINE_CSS};${TASK_MODEL_NUMBER_CELL_INLINE_CSS}">${totalSpendPerTaskText}/task</span>
  `;

  return `
    <div style="${PAGE_TOOLTIP_SHELL_INLINE_STYLE}">
      <div style="${PAGE_TOOLTIP_TITLE_INLINE_CSS}">${escapeTooltipHtml(taskName)}</div>
      <div style="${TASK_MODEL_GRID_INLINE_CSS}">${rowHtml}${totalHtml}</div>
    </div>
  `;
}
